package com.tovtalk.ui.theme

import androidx.compose.foundation.isSystemInDarkTheme
import androidx.compose.material3.MaterialTheme
import androidx.compose.material3.darkColorScheme
import androidx.compose.material3.lightColorScheme
import androidx.compose.runtime.Composable
import androidx.compose.runtime.CompositionLocalProvider
import androidx.compose.runtime.ReadOnlyComposable
import androidx.compose.runtime.remember
import androidx.compose.ui.graphics.Color

private val DarkColorScheme = darkColorScheme(
    primary = TovTalkBrand,
    secondary = TovTalkBrandLight,
    tertiary = TovTalkBrandLight,
    background = DarkBackground,
    surface = DarkSurface,
    surfaceVariant = DarkSurfaceElevated,
    error = ErrorRed,
    onPrimary = Color.White,
    onSecondary = Color.White,
    onTertiary = Color.White,
    onBackground = DarkTextPrimary,
    onSurface = DarkTextPrimary,
    onSurfaceVariant = DarkTextSecondary,
    onError = Color.White,
    outline = DarkBorder,
    outlineVariant = DarkBorder.copy(alpha = 0.5f)
)

private val LightColorScheme = lightColorScheme(
    primary = TovTalkBrand,
    secondary = TovTalkBrandLight,
    tertiary = TovTalkBrandLight,
    background = LightBackground,
    surface = LightSurface,
    surfaceVariant = LightSurfaceElevated,
    error = ErrorRed,
    onPrimary = Color.White,
    onSecondary = Color.White,
    onTertiary = Color.White,
    onBackground = LightTextPrimary,
    onSurface = LightTextPrimary,
    onSurfaceVariant = LightTextSecondary,
    onError = Color.White,
    outline = LightBorder,
    outlineVariant = LightBorder.copy(alpha = 0.5f)
)

/**
 * TovTalk Theme - Shared across Android, iOS, Web
 * Platform-specific status bar styling can be applied via expect/actual
 */
@Composable
fun TovTalkTheme(
    darkTheme: Boolean = isSystemInDarkTheme(),
    content: @Composable () -> Unit
) {
    val colorScheme = if (darkTheme) DarkColorScheme else LightColorScheme
    val extendedColors = if (darkTheme) DarkExtendedColors else LightExtendedColors
    val customFont = LocalTovTalkFontFamily.current
    val typography = remember(customFont) {
        if (customFont != null) buildTovTalkTypography(customFont) else TovTalkTypography
    }

    CompositionLocalProvider(
        LocalTovTalkColors provides extendedColors,
        LocalTovTalkTypography provides typography,
        LocalTovTalkSpacing provides TovTalkSpacing,
        LocalTovTalkSizes provides TovTalkSizes,
        LocalTovTalkRadius provides TovTalkRadius,
        LocalTovTalkShapes provides TovTalkShapes,
        LocalTovTalkAnimations provides TovTalkAnimations,
        LocalTovTalkElevation provides TovTalkElevation,
        LocalTovTalkOpacity provides TovTalkOpacity,
    ) {
        MaterialTheme(
            colorScheme = colorScheme,
            typography = MaterialTypography,
            content = content
        )
    }
}

object TovTalkTheme {
    val colors: TovTalkExtendedColors
        @Composable
        @ReadOnlyComposable
        get() = LocalTovTalkColors.current

    val typography: TovTalkTypographySystem
        @Composable
        @ReadOnlyComposable
        get() = LocalTovTalkTypography.current

    val spacing: TovTalkSpacingSystem
        @Composable
        @ReadOnlyComposable
        get() = LocalTovTalkSpacing.current

    val sizes: TovTalkSizeSystem
        @Composable
        @ReadOnlyComposable
        get() = LocalTovTalkSizes.current

    val radius: TovTalkRadiusSystem
        @Composable
        @ReadOnlyComposable
        get() = LocalTovTalkRadius.current

    val shapes: TovTalkShapeSystem
        @Composable
        @ReadOnlyComposable
        get() = LocalTovTalkShapes.current

    val animations: TovTalkAnimationSpecs
        @Composable
        @ReadOnlyComposable
        get() = LocalTovTalkAnimations.current

    val elevation: TovTalkElevationSystem
        @Composable
        @ReadOnlyComposable
        get() = LocalTovTalkElevation.current

    val opacity: TovTalkOpacitySystem
        @Composable
        @ReadOnlyComposable
        get() = LocalTovTalkOpacity.current
}
